import React, {useEffect, useState} from 'react'
import { useHistory } from 'react-router'

import { useApi, usePersistedApi, usePersistedState, useBlurFocusedInputOnUnmount } from 'hooks'
import entities from 'reduxStore/entityKeys'
import {exitReact} from 'utils'
import Api from 'api'
import { UserAccountProps } from "utils/types";

import { AvailablePositions, SortingOption, FilterOption, Filters } from 'components/AvailablePositions'
import {SortingOptions} from "components/AvailablePositions/SortingOptions";
import {FilterOptions} from "components/AvailablePositions/FilterOptions";
import TalentNavbar from 'components/TalentNavbar';
import TalentPageLayout from 'components/TalentPageLayout';
import TalentBackButton from 'components/TalentBackButton/TalentBackButton';
import EducationCredentialsConfirmDialog from 'components/EducationCredentialsConfirmDialog';
import FixedError from "components/FixedError"
import FixedLoader from "components/FixedLoader"
import Popup from "components/Popup";
import {Days, Basis} from "components/EditJob";
import {HYGIENIST_TYPES} from "utils/types";
import useQueryParams from 'hooks/useQueryParams'
import { Box, Stack, Typography } from '@mui/material';
import { ActionButton, AlertNotification, NotificationBox, NotificationPaper } from './components/alert.styles'
import { GetPublicHireJobError } from './components/errors'

// @ts-ignore
const TempStars = window.TempStars;
// @ts-ignore
const app = window?.app;

const defaultFilterBy = {
	availability: Days,
	basis: undefined
};

// Admin / associate-dentist talent land on the Permanent Job Board as their home
// surface and must never reach the temping calendar. They have nowhere valid to go
// "back" to from here, so the back button is hidden for them entirely (T2-6271).
// Hygienists / assistants reach the Job Board from their temping home, so for them
// Back returns to the temping home.
export const shouldShowJobPostsListBackButton = (isAdminOrAssociate: boolean) => !isAdminOrAssociate;

// Job Board page with back button navigation
export default function JobPostsListPage() {
	const { jobPost, src: alertSource } = useQueryParams();
	const history = useHistory()
	app.hidePreloader()
	useBlurFocusedInputOnUnmount()

	const [jobs, loading, error, getAllHireJobs] = usePersistedApi({
		apiMethod: Api.getAvailableHireJobs,
		entityName: entities.hygienistJobs,
		initialValue: [],
	})

	const [
		jobPostFromQueryParam,
		jobPostFromQueryParamLoading,
		jobPostFromQueryParamError,
		getJobPostFromQueryParam
	] = useApi({
		apiMethod: Api.getPublicHireJobByIdForHygienist,
		initialValue: null,
	})
	const jpqError: GetPublicHireJobError | null = jobPostFromQueryParamError;
	const [isErrorAlertWasClosed, setIsErrorAlertWasClosed] = useState<boolean>(false);
	const [educationCredentialsSaved, setEducationCredentialsSaved] = useState<boolean>(false);

	const [
		user,
		hygienistUserLoading,
		hygienistUserError,
		getHygienistUser
	  ] = usePersistedApi({
		apiMethod: Api.getUser,
		entityName: entities.user,
		initialValue: {} as UserAccountProps
	  })

	useEffect(() => {
		getAllHireJobs({})
		getHygienistUser({})
		TempStars.Analytics.track('Viewed Job Board');
	}, [getAllHireJobs, getHygienistUser])

	useEffect(() => {
		if (!jobPost) {
			return;
		}

		function deleteQueryParam(param: string) {
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.delete(param);
			window.history.replaceState(null, '', newUrl);
			return;
		}

		const jobPostId = Number(jobPost);
		if (!Number.isSafeInteger(jobPostId) || jobPostId < 0) {
			deleteQueryParam("jobPost");
			return;
		}

		getJobPostFromQueryParam(jobPostId);
		deleteQueryParam("jobPost");
	}, [jobPost, alertSource])

	useEffect(() => {
		if (!jobPostFromQueryParam || !user) {
			return;
		}

		history.push({
			pathname: 'jobPostDetails',
			state: {
				job: jobPostFromQueryParam,
				user,
				alertSource,
			},
		});
  }, [jobPostFromQueryParam, user, history, alertSource]);


	const [sortByPopupOpened, setSortByPopupOpened] = useState(false)
	const [sortBy, setSortBy] = usePersistedState({
		entityName: entities.hygienistJobListSortBy,
		initialValue: SortingOption.datePosted,
	})

	const onSortByPopupOpen = () => {
		setSortByPopupOpened(true)
	}
	const onSortByPopupClose = () => {
		setSortByPopupOpened(false)
	}

	const onSortOptionSelect = (option: SortingOption) => {
		setSortBy(option);
		onSortByPopupClose();
	}

	const [filterByPopupOpened, setFilterByPopupOpened] = useState(false)
	const [filterBy, setFilterBy] = usePersistedState<Filters>({
		entityName: entities.hygienistJobListFilterBy,
		initialValue: defaultFilterBy,
	})

	const [availabilityFilter, setAvailabilityFilter] = useState<FilterOption[]>(filterBy.availability)
	const onSetAvailabilityFilter = (id: number, checked: boolean) => {
		setAvailabilityFilter((prevState => {
			if (!checked) {
				return prevState.filter(option => option.id !== id)
			} else {
				return [...prevState, Days[id]]
			}
		}))
	}

	const [basisFilter, setBasisFilter] = useState<FilterOption | undefined>(filterBy.basis);
	const onSetBasisFilter = (basisId: number) => {
		setBasisFilter(Basis.find(({id}) => id === basisId) as FilterOption);
	}

	/** T2-5785: reset filters/sort when opening Job Board (including after full page refresh) */
	useEffect(() => {
		setFilterBy(defaultFilterBy)
		setSortBy(SortingOption.datePosted)
		setAvailabilityFilter(defaultFilterBy.availability)
		setBasisFilter(defaultFilterBy.basis)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onFilterByPopupOpen = () => {
		setFilterByPopupOpened(true)
	}
	const onFilterByPopupClose = () => {
		setFilterByPopupOpened(false)
	}

	const onFiltersApply = () => {
		setFilterBy({
			availability: availabilityFilter,
			basis: basisFilter
		});
		onFilterByPopupClose();
	}

	const onFiltersReset = () => {
		setBasisFilter(defaultFilterBy.basis);
		setAvailabilityFilter(defaultFilterBy.availability);
		setFilterBy({
			availability: defaultFilterBy.availability,
			basis: defaultFilterBy.basis
		});
		onFilterByPopupClose();
	}

	const isAdminOrAssociate = user?.hygienist?.type === HYGIENIST_TYPES.ASSOCIATE_DENTIST
		|| user?.hygienist?.type === HYGIENIST_TYPES.FRONT_DESK;

	// Get user info for navbar
	const hygienist = user?.hygienist;
	const firstName = hygienist?.firstName || '';
	const lastName = hygienist?.lastName || '';
	const photoUrl = hygienist?.photoUrl || undefined;
	const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST || 
	                    hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;
	const shouldShowEducationCredentialsDialog = Boolean(
		hygienist
		&& !hygienist.educationCredentialsConfirmedOn
		&& !educationCredentialsSaved
	);

	const handleBack = () => {
		// Only rendered for hygienists / assistants (see shouldShowJobPostsListBackButton),
		// who arrive at the Job Board from their temping home.
		history.push({ pathname: 'hygienistHome' });
	};

	return (
		<>
			<TalentPageLayout
				navbar={(
					<TalentNavbar
						firstName={firstName}
						lastName={lastName}
						photoUrl={photoUrl}
						isHygOrAsst={isHygOrAsst}
						instabookActivated={hygienist?.InstabookActivated === 1}
						isTestAccount={(hygienist as any)?.isTest === 1}
						showAddShiftButton={false}
						showJobBoardButton={false}
					/>
				)}
			>
			{shouldShowJobPostsListBackButton(isAdminOrAssociate) && (
				<TalentBackButton onBack={handleBack} />
			)}
			<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px' }}>
				<button type="button" data-testid="job-board-alert-settings" onClick={() => history.push({ pathname: 'jobAlerts' })}
					style={{ color: '#0879d3', background: '#fff', border: '1px solid #0879d3', borderRadius: 8, padding: '9px 14px', fontWeight: 700 }}>
					Job Alerts
				</button>
			</div>
			<AvailablePositions
				jobs={jobs}
				sortBy={sortBy}
				filterBy={filterBy}
				onSortButtonClick={onSortByPopupOpen}
				onFilterButtonClick={onFilterByPopupOpen}
				hygienistType={user?.hygienist?.type}
				loading={loading || hygienistUserLoading}
			/>
			<Popup
				opened={sortByPopupOpened}
				onClose={onSortByPopupClose}
			>
				<SortingOptions onOptionSelect={onSortOptionSelect} />
			</Popup>
			<Popup
				opened={filterByPopupOpened}
				onClose={onFilterByPopupClose}
			>
				<FilterOptions
					availabilityFilter={availabilityFilter}
					onSetAvailabilityFilter={onSetAvailabilityFilter}
					basisFilter={basisFilter}
					onSetBasisFilter={onSetBasisFilter}
					onClose={onFilterByPopupClose}
					onFiltersApply={onFiltersApply}
					onFiltersReset={onFiltersReset}
				/>
			</Popup>
			</TalentPageLayout>
			<FixedLoader
				loading={loading || hygienistUserLoading}
			/>
			<FixedError
				error={error}
			/>
			<Popup
			  opened={!!jpqError && !isErrorAlertWasClosed}
			  onClose={() => setIsErrorAlertWasClosed(true)}
			>
				<NotificationBox>
					<AlertNotification>
						Thanks for your interest!
					</AlertNotification>
					<NotificationPaper>
						<Typography variant="body2" sx={{ fontWeight: 'medium', color: '#1c4ed8' }}>
							<Box sx={{ color: '#1d40af', mb: 1 }}>
								Not quite a match, but don't worry!
							</Box>
							It looks like this position isn't a great match right now, but we have hundreds of other postings. 
							Let's head over to view Active Postings that are a good fit for you!
						</Typography>
					</NotificationPaper>

					<Stack direction="row" spacing={2}>
						<ActionButton onClick={() => setIsErrorAlertWasClosed(true)}>
							View Matching Positions
						</ActionButton>
					</Stack>
				</NotificationBox>
			</Popup>
			<EducationCredentialsConfirmDialog
				open={shouldShowEducationCredentialsDialog}
				hygienist={hygienist}
				onSaved={() => {
					setEducationCredentialsSaved(true);
					getHygienistUser({});
				}}
			/>
		</>
	)
}
